import { Button } from "../../../shared/ui/button";
import { Title } from "../../../shared/ui/title";
import { Auth } from "../auth/auth";
import { Modal } from "../../../shared/ui/modal";
import { RequestUnderProcess } from "../../../shared/ui/modal-notifications/request-under-process";
import { AlreadySubscribed } from "../../../shared/ui/modal-notifications/already-subscribed";
import { GenericError } from "../../../shared/ui/modal-notifications/generic-error";
import cx from "classnames";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";

import {
  useGenerateMutation,
  useLazyGetUserQuery,
  useSubscribeDirectMutation,
} from "../../data-access/store/services/auth.service";
import { useEffect, useState } from "react";
import { setMessage } from "../../../shared/data-access/store/message/messageSlice";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import {
  selectInit,
  selectLanguage,
  selectTranslations,
} from "../../../shared/data-access/store/general/generalSlice";
// import { useNavigate } from "react-router-dom";
import { checkTranslationKey } from "../../../utils/translation";
import { useLocation, useNavigate } from "react-router-dom";
import {
  selectUser,
  tokenReceived,
  updateIsFirstSignIn,
} from "../../data-access/store/authSlice";
import { UpstreamServiceBanner } from "../../../shared/ui/upstream-service-banner";

export function Pin() {
  const dispatch = useAppDispatch();
  // const navigate = useNavigate();
  const [
    subscribeDirect,
    { isLoading: subscribeDirectIsLoading, error: subscribeDirectError },
  ] = useSubscribeDirectMutation();
  const init = useAppSelector(selectInit);
  const lang = useAppSelector(selectLanguage);
  const translations = useAppSelector(selectTranslations);
  const navigate = useNavigate();
  const { state } = useLocation();
  const user = useAppSelector(selectUser);
  const [getUser] = useLazyGetUserQuery();
  const [generate] = useGenerateMutation();
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [showAlreadySubscribedModal, setShowAlreadySubscribedModal] =
    useState(false);
  const [showGenericErrorModal, setShowGenericErrorModal] = useState(false);
  const [pinValidationError, setPinValidationError] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (subscribeDirectIsLoading) {
      setShowProcessingModal(true);
    }
  }, [subscribeDirectIsLoading]);

  useEffect(() => {
    if (subscribeDirectError) {
      const errorMsg =
        (subscribeDirectError as { data?: Error })?.data?.message ||
        checkTranslationKey(translations[lang]?.Error, "Error!");
      const status = (subscribeDirectError as { status?: number })?.status;
      const lower = errorMsg.toLowerCase();
      const isWrongPin =
        status === 401 ||
        status === 403 ||
        status === 422 ||
        lower.includes("invalid pin") ||
        lower.includes("wrong pin") ||
        lower.includes("incorrect pin") ||
        lower.includes("invalid password") ||
        lower.includes("wrong password") ||
        lower.includes("incorrect password") ||
        lower.includes("otp validation failed") ||
        lower.includes("otp validation") ||
        lower.includes("validation failed") ||
        lower.includes("otp");

      if (isWrongPin) {
        setPinValidationError(errorMsg);
        dispatch(setMessage({ content: errorMsg, type: "error" }));
      } else {
        setShowGenericErrorModal(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribeDirectError]);

  const pinMaxLength = init?.users?.pin?.maxLength || 6;
  const pinMinLength = init?.users?.pin?.minLength || 4;

  const SignInSchema = Yup.object().shape({
    pin_subscribe: Yup.string()
      .min(
        pinMinLength,
        checkTranslationKey(
          translations[lang]?.The_pin_is_too_short,
          "The password is too short."
        )
      )
      .max(
        pinMaxLength,
        checkTranslationKey(
          translations[lang]?.The_pin_is_too_long,
          "The password is too long."
        )
      )
      .required(
        checkTranslationKey(
          translations[lang]?.The_pin_is_required,
          "The password is required."
        )
      ),
  });

  return (
    <div className="auth-page subscribe-page">
      <Auth
        className="auth-shell-content"
        header={
          <Title backURL="/auth/subscribe">
            {checkTranslationKey(translations[lang]?.Subscribe, "Subscribe")}
          </Title>
        }
        hasFooter
      >
        <div className="form-title form-title-login">
          {checkTranslationKey(
            translations[lang]?.Create_your_account_and_subscribe,
            "Create your account and subscribe"
          )}
        </div>
        <div className="form-container">
          <Formik
            initialValues={{ pin_subscribe: "" }}
            validationSchema={SignInSchema}
            onSubmit={async (values) => {
              try {
                setPinValidationError(null);
                const result = await subscribeDirect({
                  msisdn: state as string,
                  password: values.pin_subscribe,
                }).unwrap();
                setShowProcessingModal(false);
                if (
                  result.alreadyActive ||
                  result.activationResult === "USER_ALREADY_SUBSCRIBED"
                ) {
                  setShowAlreadySubscribedModal(true);
                } else if (user) {
                  await getUser();
                  navigate("/profile");
                } else if (result.apikey) {
                  // New guest activation: auto-login and treat as a first-timer.
                  localStorage.setItem("accessToken", result.apikey);
                  dispatch(tokenReceived(result.apikey));
                  dispatch(updateIsFirstSignIn(true));
                  await getUser();
                  navigate("/");
                } else {
                  navigate("/auth/login");
                }
              } catch {
                setShowProcessingModal(false);
              }
            }}
          >
            {({ errors, touched }) => (
              <>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                <Form {...({} as any)}>
                  <div
                    className={cx("form-input-row", {
                      danger: errors.pin_subscribe && touched.pin_subscribe,
                      success: !errors.pin_subscribe,
                    })}
                  >
                    <label className="label" htmlFor="pin_subscribe">
                      {checkTranslationKey(
                        translations[lang]?.Please_enter_your_pin,
                        "Please enter your password"
                      )}
                    </label>
                    <Field
                      type="text"
                      name="pin_subscribe"
                      className="full-input"
                      placeholder={checkTranslationKey(
                        translations[lang]?.Your_code,
                        "Your code"
                      )}
                      autoComplete="off"
                      maxLength={pinMaxLength}
                      required
                    />
                    <ErrorMessage name="pin_subscribe">
                      {(msg) => (
                        <div className="error-msg">
                          <img
                            src="/img/icns/alert-fill.svg"
                            alt="alert"
                            className="color-svg"
                          />
                          {msg}
                        </div>
                      )}
                    </ErrorMessage>
                    {pinValidationError &&
                      !(errors.pin_subscribe && touched.pin_subscribe) && (
                        <div className="error-msg">
                          <img
                            src="/img/icns/alert-fill.svg"
                            alt="alert"
                            className="color-svg"
                          />
                          {checkTranslationKey(
                            translations[lang]?.Wrong_code,
                            "Wrong OTP!"
                          )}
                        </div>
                      )}
                  </div>

                  {pinValidationError && (
                    <div
                      className="forgot-area"
                      style={{ paddingTop: "0", paddingBottom: "1em" }}
                    >
                      <a
                        href="#"
                        className="forgot-password"
                        onClick={async (e) => {
                          e.preventDefault();
                          try {
                            await generate({
                              msisdn: state as string,
                            }).unwrap();
                            setPinValidationError(null);
                          } catch {
                            setShowGenericErrorModal(true);
                          }
                        }}
                      >
                        {checkTranslationKey(
                          translations[lang]?.Resend_code,
                          "Resend code"
                        )}
                      </a>
                    </div>
                  )}

                  <div>
                    <div className="submit">
                      <Button
                        buttonType="success"
                        type="submit"
                        value="Submit"
                        disabled={subscribeDirectIsLoading}
                      >
                        {subscribeDirectIsLoading
                          ? checkTranslationKey(
                              translations[lang]?.Submitting,
                              "Submitting..."
                            )
                          : checkTranslationKey(
                              translations[lang]?.Confirm,
                              "Confirm"
                            )}
                      </Button>
                    </div>
                    <p className="cost">
                      {checkTranslationKey(
                        translations[lang]?.Cost_per_day,
                        "Cost: 150 AOA/day"
                      )}
                    </p>
                    <p className="cost-free">
                      {checkTranslationKey(
                        translations[lang]?.First_day_free,
                        "1st day free"
                      )}
                    </p>
                    {/*<div className="terms-area">*/}
                    {/*  {checkTranslationKey(*/}
                    {/*    translations[lang]?.By_entering_the_pin_you_confirm,*/}
                    {/*    "By entering the password you confirm subscription and agree to the"*/}
                    {/*  )}{" "}*/}
                    {/*  <a href={init?.content?.terms[lang]} target="_blank">*/}
                    {/*    {checkTranslationKey(*/}
                    {/*      translations[lang]?.Terms_and_Conditions,*/}
                    {/*      "Terms and Conditions"*/}
                    {/*    )}*/}
                    {/*  </a>*/}
                    {/*</div>*/}
                  </div>
                </Form>

                <Modal
                  show={showProcessingModal}
                  setShow={setShowProcessingModal}
                  hideCloseButton
                >
                  <RequestUnderProcess />
                </Modal>

                <Modal
                  show={showAlreadySubscribedModal}
                  setShow={setShowAlreadySubscribedModal}
                  hideCloseButton
                >
                  <AlreadySubscribed
                    onButtonClick={() => {
                      setShowAlreadySubscribedModal(false);
                      navigate("/");
                    }}
                  />
                </Modal>

                <Modal
                  show={showGenericErrorModal}
                  setShow={setShowGenericErrorModal}
                  hideCloseButton
                >
                  <GenericError
                    onButtonClick={() => {
                      setShowGenericErrorModal(false);
                      navigate("/");
                    }}
                  />
                </Modal>
              </>
            )}
          </Formik>
        </div>
      </Auth>
      <div className="stream-service">
        <UpstreamServiceBanner />
      </div>
    </div>
  );
}

export default Pin;
